/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';
import { constants } from '../../constants/ApplicationConstants';
import { LinkProps } from './Link.types';
import { getClickTrackValue, isDcaTrackingPayloadReady, trackImpression } from '../../utils/TrackingPropertyUtils';

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      id,
      text,
      linkHref,
      ariaLabel,
      rel,
      linkClassName,
      trackingProperties,
      callback,
      target = '_blank',
      custom_click_track_value,
      compName,
      linkType = 'external',
      children,
      // when anchor tags are not given an href they are not focusable, this prevents that from happening in the case of an authoring mistake
      tabIndex = 0,
      touchCallback,
      role = 'link',
      ...props
    },
    ref: any
  ) => {
    // WEB-191033: Prefer an explicit boolean `isCoBrand` from trackingProperties when provided
    // (e.g. normalizers that drive cobrand-only cards). Fall back to deriving from `type` or
    // `merchandisingCategory` for legacy consumers that don't set `isCoBrand` directly.
    const trackingProps = {
      ...trackingProperties,
      isCoBrand:
        typeof trackingProperties?.isCoBrand === 'boolean'
          ? trackingProperties.isCoBrand
          : trackingProperties?.type
          ? trackingProperties?.type === 'CoBrand'
          : trackingProperties?.merchandisingCategory === constants.COBRAND_NON_POINTS_MATH,
    };
    const linkRef = useRef<HTMLAnchorElement>(null);
    //assign linkRef to the ref coming from props
    useImperativeHandle(ref, () => ref?.current ?? (linkRef.current as HTMLAnchorElement));

    // WEB-191033: DCA-driven links render BEFORE the Adobe Target mbox resolves.
    // The first invocation sees empty tracking values; we must NOT stamp
    // `track-completed` in that state, otherwise the re-render with real Target
    // content is silently gated out. Depend on the tracking inputs so the effect
    // re-runs when Target resolves, and gate DCA elements on a content-ready
    // check. Non-DCA (legacy) elements keep the original mount-only behavior.
    useEffect(() => {
      if (linkRef?.current?.classList?.contains('track-completed')) return;

      if (!isDcaTrackingPayloadReady(trackingProps, custom_click_track_value)) return;

      if (!custom_click_track_value) {
        getClickTrackValue(linkType, trackingProps, linkRef);
      }
      trackImpression(trackingProps || {}, text || '', props?.preTrackVal);
      linkRef?.current?.classList?.add('track-completed');
    }, [trackingProperties, custom_click_track_value, linkType, text, props?.preTrackVal]);

    return (
      <a
        data-component-name="a-ui-library-Link"
        data-testid="ui-library-Link"
        id={id}
        onClick={callback}
        onTouchEnd={touchCallback}
        target={target}
        href={linkHref}
        rel={rel}
        className={clsx(
          linkClassName,
          trackingProps?.clickTrack ? 'custom_click_track' : '',
          trackingProps?.impressionTrack === false ? 'no-impression' : ''
        )}
        aria-label={ariaLabel}
        ref={linkRef || ref}
        data-tracking-content-position={trackingProps?.trackingContentPosition}
        data-tracking-offer-type={trackingProps?.trackingOfferType}
        data-at-c-ce-var48={trackingProps?.atCCeVar48}
        data-tracking-description={trackingProps?.trackingDescription}
        data-tracking-tag={trackingProps?.trackingTag}
        data-comp-name={compName}
        data-custom_click_track_value={custom_click_track_value}
        data-is-co-brand={trackingProps?.isCoBrand}
        data-is-click-track={trackingProps?.clickTrack}
        data-is-impression-track={trackingProps?.impressionTrack}
        data-is-impression-count={trackingProps?.impressionCount}
        tabIndex={tabIndex}
        role={role}
      >
        {text}
        {children}
      </a>
    );
  }
);
